/**
 * LanguageSwitcher — compact globe-icon dropdown for EN/ES.
 *
 * Persists to localStorage via i18next-browser-languagedetector. Available on
 * both the marketing top-nav (Landing) and the authenticated dashboard shell.
 */
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";

const LANGS = [
  { code: "en", name: "English", flag: "🇺🇸" },
  { code: "es", name: "Español", flag: "🇲🇽" },
];

export default function LanguageSwitcher({ variant = "nav" }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = i18n.language?.startsWith("es") ? "es" : "en";
  const currentLang = LANGS.find((l) => l.code === current) || LANGS[0];

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const change = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  const isDashboard = variant === "dashboard";
  const isLight = variant === "light";
  const btnClass = isLight
    ? "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs text-slate-600 hover:text-slate-900 border border-slate-300 hover:border-slate-400 bg-white transition-colors"
    : isDashboard
    ? "inline-flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
    : "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs text-slate-300 hover:text-white border border-white/10 hover:border-white/20 transition-colors";

  return (
    <div className="relative" ref={ref} data-testid="language-switcher">
      <button
        onClick={() => setOpen(!open)}
        className={btnClass}
        aria-label="Change language"
        data-testid="language-switcher-btn"
      >
        <Globe size={13} strokeWidth={2} />
        <span className="uppercase font-semibold tracking-widest">{currentLang.code}</span>
      </button>
      {open && (
        <div
          className={`absolute right-0 mt-2 w-40 rounded-md shadow-2xl overflow-hidden z-50 ${
            isLight
              ? "bg-white border border-slate-200"
              : "bg-[var(--ld-surface-2)] border border-white/10"
          }`}
          data-testid="language-switcher-menu"
        >
          {LANGS.map((l) => (
            <button
              key={l.code}
              onClick={() => change(l.code)}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left transition-colors ${
                l.code === current
                  ? "bg-[var(--ld-royal)]/10 text-[var(--ld-royal-hover)]"
                  : isLight
                  ? "text-slate-700 hover:bg-slate-50"
                  : "text-slate-300 hover:bg-white/5"
              }`}
              data-testid={`language-option-${l.code}`}
            >
              <span className="flex items-center gap-2">
                <span className="text-base leading-none">{l.flag}</span>
                {l.name}
              </span>
              {l.code === current && <Check size={13} strokeWidth={2.5} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
